export const UPLOAD_PROGRESS_METADATA_KEY = 'onUploadProgress';

export interface UploadProgressEvent {
  loaded: number;
  total: number;
  lengthComputable: boolean;
}

export type UploadProgressCallback = (event: UploadProgressEvent) => void;

interface FetchInitWithUploadProgress extends RequestInit {
  onUploadProgress?: UploadProgressCallback;
}

const getRequestUrl = (input: RequestInfo | URL): string => {
  if (typeof input === 'string') {
    return input;
  }
  if (input instanceof URL) {
    return input.toString();
  }
  return input.url;
};

const createAbortError = (): DOMException =>
  new DOMException('The operation was aborted.', 'AbortError');

const fetchWithXmlHttpRequest = (
  input: RequestInfo | URL,
  init: FetchInitWithUploadProgress,
  onUploadProgress: UploadProgressCallback,
  body: FormData
): Promise<Response> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(init.method ?? 'POST', getRequestUrl(input));

    if (init.credentials === 'include') {
      xhr.withCredentials = true;
    }

    new Headers(init.headers).forEach((value, key) => {
      if (key.toLowerCase() !== 'content-type') {
        xhr.setRequestHeader(key, value);
      }
    });

    xhr.upload.onprogress = (event) => {
      onUploadProgress({
        loaded: event.loaded,
        total: event.total,
        lengthComputable: event.lengthComputable,
      });
    };

    xhr.onload = () => {
      const responseHeaders = new Headers();
      const headerLines = xhr.getAllResponseHeaders().trim();
      if (headerLines.length > 0) {
        headerLines.split(/[\r\n]+/).forEach((line) => {
          const separatorIndex = line.indexOf(':');
          if (separatorIndex === -1) {
            return;
          }
          responseHeaders.append(
            line.slice(0, separatorIndex).trim(),
            line.slice(separatorIndex + 1).trim()
          );
        });
      }

      resolve(
        new Response(xhr.responseText, {
          status: xhr.status,
          statusText: xhr.statusText,
          headers: responseHeaders,
        })
      );
    };

    xhr.onerror = () => {
      reject(new TypeError('Network request failed'));
    };
    xhr.onabort = () => {
      reject(createAbortError());
    };

    if (init.signal) {
      if (init.signal.aborted) {
        xhr.abort();
        reject(createAbortError());
        return;
      }

      init.signal.addEventListener(
        'abort',
        () => {
          xhr.abort();
        },
        { once: true }
      );
    }

    xhr.send(body);
  });
};

export const fetchWithUploadProgress: typeof fetch = (input, init) => {
  const { onUploadProgress, ...fetchInit } = (init ??
    {}) as FetchInitWithUploadProgress;

  if (
    typeof onUploadProgress !== 'function' ||
    !(fetchInit.body instanceof FormData)
  ) {
    return fetch(input, fetchInit);
  }

  return fetchWithXmlHttpRequest(
    input,
    fetchInit,
    onUploadProgress,
    fetchInit.body
  );
};
